import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import PaginatedGentlyTable, {
  TableColumn,
} from 'src/components/common/PaginatedGentlyTable';
import Page from 'src/components/page/Page';
import MDTypography from 'src/components/md/MDTypography';
import usePaginatedCompanies from 'src/api/core/companies/usePaginatedCompanies';
import { usePaginationState } from 'src/hooks/usePaginationState';
import { useDebounce } from 'src/hooks/useDebounce';
import { CompanyDTO } from 'src/api/core/companies/useCompany';
import useCreateCompany from 'src/api/core/companies/useCreateCompany';
import useDeleteCompany from 'src/api/core/companies/useDeleteCompany';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import {
  MultiSelect,
  MultiSelectOption,
} from 'src/components/common/MultiSelect';
import { MODULE_TYPES } from '@erp/shared-types';

import BulkCSVUpload, {
  UploadResult,
} from 'src/components/common/BulkCSVUpload';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardAction,
} from '@/components/ui/card';
import { PlusIcon, UploadIcon, TrashIcon } from '@phosphor-icons/react';
import { useIsModuleEnabled } from 'src/hooks/organization-config/useOrganizationEnabledModules';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from 'src/utils/user';
import CompanyCreateModal from 'src/components/company/CompanyCreateModal';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface CompaniesPageProps {
  module?: string;
}

const CompaniesPage = ({ module }: CompaniesPageProps) => {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyDTO>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<CompanyDTO>();
  const createCompany = useCreateCompany();
  const deleteCompanyMutation = useDeleteCompany(
    companyToDelete?.id || '',
    () => {
      refetch();
      setDeleteConfirmOpen(false);
      setCompanyToDelete(undefined);
    },
  );
  const { isModuleEnabled: _isModuleEnabled } = useIsModuleEnabled();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdminUser = isAdmin(currentUser, viewMode);

  // Filter state
  const [selectedModules, setSelectedModules] = useState<MultiSelectOption[]>(
    [],
  );
  const [selectedSupplierTags, setSelectedSupplierTags] = useState<
    MultiSelectOption[]
  >([]);

  // Pagination state management
  const pagination = usePaginationState({
    initialPageSize: 100,
  });

  // Debounce search query to avoid too many API calls
  const debouncedSearchQuery = useDebounce(pagination.searchQuery, 300);

  // Fetch supplier tags enum
  const { data: supplierTagsEnum } = useCoreEnums(
    {
      name: 'Supplier Tags',
      includeOptions: true,
    },
    {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  );

  // Available module options
  const moduleOptions: MultiSelectOption[] = useMemo(
    () => [
      {
        id: MODULE_TYPES.REBATES,
        label: 'Rebates',
        value: MODULE_TYPES.REBATES,
      },
    ],
    [],
  );

  // Available supplier tag options
  const supplierTagOptions: MultiSelectOption[] = useMemo(() => {
    if (!supplierTagsEnum?.edges?.[0]?.options) return [];
    return supplierTagsEnum.edges[0].options.map((opt) => ({
      id: opt.id,
      label: opt.name || '',
      value: opt.id,
    }));
  }, [supplierTagsEnum]);

  // API query with pagination
  // Use offset-based pagination for better UX and reliability with UUIDs
  const { data, isLoading, error, refetch } = usePaginatedCompanies({
    first: pagination.pageSize,
    offset: pagination.getCurrentOffset(),
    search: debouncedSearchQuery || undefined,
    modules:
      selectedModules.length > 0
        ? selectedModules.map((m) => m.value)
        : undefined,
    supplierTagIds:
      selectedSupplierTags.length > 0
        ? selectedSupplierTags.map((t) => t.id)
        : undefined,
    sortBy: pagination.sortBy,
    sortDirection: pagination.sortDirection,
  });

  // Update pagination state when data changes
  useEffect(() => {
    if (data?.pageInfo) {
      pagination.setHasNextPage(data.pageInfo.hasNextPage);
      pagination.setPaginationInfo({
        totalCount: data.pageInfo.totalCount,
        totalPages: data.pageInfo.totalPages,
        currentPage: data.pageInfo.currentPage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.pageInfo]);

  // Reset pagination to first page when search or filters change
  const [previousSearch, setPreviousSearch] = useState('');
  const [previousModules, setPreviousModules] = useState<MultiSelectOption[]>(
    [],
  );
  const [previousTags, setPreviousTags] = useState<MultiSelectOption[]>([]);

  useEffect(() => {
    const searchChanged = debouncedSearchQuery !== previousSearch;
    const modulesChanged =
      JSON.stringify(selectedModules) !== JSON.stringify(previousModules);
    const tagsChanged =
      JSON.stringify(selectedSupplierTags) !== JSON.stringify(previousTags);

    if (searchChanged || modulesChanged || tagsChanged) {
      setPreviousSearch(debouncedSearchQuery);
      setPreviousModules(selectedModules);
      setPreviousTags(selectedSupplierTags);

      if (pagination.currentPage > 1) {
        pagination.goToFirstPage();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchQuery,
    selectedModules,
    selectedSupplierTags,
    previousSearch,
    previousModules,
    previousTags,
  ]);

  // Expected CSV columns for bulk company upload
  const expectedColumns = ['name', 'email', 'modules'];

  // Handler for delete confirmation
  const handleDeleteCompany = (company: CompanyDTO) => {
    setCompanyToDelete(company);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteCompany = () => {
    if (companyToDelete) {
      deleteCompanyMutation.mutate();
    }
  };

  const columns: TableColumn<CompanyDTO>[] = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
    },
    {
      id: 'email',
      label: 'Email',
      sortable: true,
    },
    {
      id: 'internalCode' as keyof CompanyDTO,
      label: 'Internal Code',
      render: (value: unknown, row: CompanyDTO) => {
        const rexelName = (row.metadata as Record<string, unknown>)?.rexel_code;
        return rexelName ? String(rexelName) : '-';
      },
    },
    {
      id: 'primaryAddress',
      label: 'Address',
      render: (value: unknown, row: CompanyDTO) => {
        if (!row.primaryAddress || !row.primaryAddress.addressLine1) return '-';
        return `${row.primaryAddress.addressLine1}`;
      },
    },
    {
      id: 'supplierTags' as keyof CompanyDTO,
      label: 'Tags',
      sortable: true,
      render: (value: unknown, row: CompanyDTO) => {
        const tags = row.supplier?.tags || [];
        if (tags.length === 0) return <span>-</span>;

        const visibleTags = tags.slice(0, 3);
        const remainingCount = tags.length - visibleTags.length;

        return (
          <TooltipProvider>
            <div className="flex gap-1 items-center flex-wrap">
              {visibleTags.map((tag) => (
                <Badge key={tag.id} variant="secondary" className="text-xs">
                  {tag.name}
                </Badge>
              ))}
              {remainingCount > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-xs cursor-help">
                      +{remainingCount} more
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-1">
                      {tags.slice(3).map((tag) => (
                        <div key={tag.id} className="text-xs">
                          {tag.name}
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        );
      },
    },
    {
      id: 'id',
      label: 'Actions',
      minWidth: isAdminUser ? '80px' : undefined,
      render: (value, row) => (
        <div onClick={(e) => e.stopPropagation()} className="flex gap-1">
          {isAdminUser && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteCompany(row)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Delete company"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const handleRowClick = (company: CompanyDTO) => {
    navigate({ to: `/companies/${company.id}` });
  };

  const handleBulkUpload = () => {
    setBulkUploadOpen(true);
  };

  // Handle individual row upload for bulk CSV
  const handleUploadRow = async (
    rowData: Record<string, unknown>,
    index: number,
  ): Promise<void> => {
    try {
      // Parse modules from CSV (comma-separated string or empty)
      let modules: string[] = [];
      if (rowData.modules && typeof rowData.modules === 'string') {
        modules = rowData.modules
          .split(',')
          .map((m) => m.trim())
          .filter((m) => m.length > 0);
      }

      // Transform CSV row data to match API format
      const companyData = {
        name: String(rowData.name || ''),
        email: rowData.email ? String(rowData.email) : undefined,
        modules,
      };

      // Call the API to create company
      await createCompany.mutateAsync(companyData);

      console.log(
        `Successfully uploaded company ${index + 1}:`,
        companyData.name,
      );
    } catch (error) {
      console.error(`Failed to upload company ${index + 1}:`, error);
      throw error; // Re-throw to trigger failed status
    }
  };

  // Handle completion of bulk upload
  const handleBulkUploadComplete = (results: UploadResult[]) => {
    const successful = results.filter((r) => r.status === 'success').length;
    const failed = results.filter((r) => r.status === 'failed').length;

    console.log(
      `Bulk upload completed: ${successful} successful, ${failed} failed`,
    );

    // Refresh the companies list and reset pagination
    pagination.reset();
    refetch();

    // Close modal only if all uploads succeeded
    if (failed === 0) {
      setBulkUploadOpen(false);
    }

    // Individual API calls will show their own success/error toasts via useMutationApi
  };

  const tableActions = (
    <div className="flex gap-2">
      {isAdminUser && (
        <Button onClick={handleBulkUpload} variant="outline">
          <UploadIcon className="mr-2 h-4 w-4" />
          Bulk Upload
        </Button>
      )}
      {isAdminUser && (
        <Button
          onClick={() => {
            setEditingCompany(undefined);
            setShowCreateModal(true);
          }}
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Create Company
        </Button>
      )}
    </div>
  );

  // Dynamic properties
  const pageTitle = 'Companies';
  const searchPlaceholder = 'Search companies...';
  const emptyMessage = 'No companies found';

  // Check if any filters are active
  const hasActiveFilters =
    selectedModules.length > 0 || selectedSupplierTags.length > 0;

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedModules([]);
    setSelectedSupplierTags([]);
  };

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      enabled: true,
      pagination: pagination.paginationState,
      onPageSizeChange: pagination.setPageSize,
      onPreviousPage: pagination.goToPreviousPage,
      onNextPage: () => pagination.goToNextPage(),
      onFirstPage: pagination.goToFirstPage,
      onLastPage: data?.pageInfo?.totalPages
        ? () => pagination.goToPage(data.pageInfo.totalPages)
        : undefined,
      onGoToPage: pagination.goToPage,
      showRowsPerPage: true,
      showPageInfo: true,
      showPageNumbers: true,
      rowsPerPageOptions: [20, 30, 50, 100],
    }),
    [pagination, data?.pageInfo?.totalPages],
  );

  // Check if we're on a child route (not the index page itself)
  const location = useLocation();
  const isChildRoute = location.pathname !== '/companies';

  // If we're on a child route, just render the Outlet
  if (isChildRoute) {
    return <Outlet />;
  }

  return (
    <>
      <Page isLoading={isLoading} error={error ?? undefined}>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">{pageTitle}</CardTitle>
            <CardAction>{tableActions}</CardAction>
          </CardHeader>
          <CardContent>
            {/* Filters Section */}
            <div className="mb-4 flex flex-wrap gap-3 items-end justify-between">
              <div className="flex gap-3 items-end">
                <div className="w-[240px]">
                  <MultiSelect
                    label="Supplier Modules"
                    options={moduleOptions}
                    value={selectedModules}
                    onChange={setSelectedModules}
                    placeholder="Filter by modules..."
                    hideChips={true}
                    size="sm"
                  />
                </div>
                <div className="w-[240px]">
                  <MultiSelect
                    label="Supplier Tags"
                    options={supplierTagOptions}
                    value={selectedSupplierTags}
                    onChange={setSelectedSupplierTags}
                    placeholder="Filter by tags..."
                    hideChips={true}
                    size="sm"
                  />
                </div>
              </div>
              {hasActiveFilters && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleClearFilters}
                  className="mb-1"
                >
                  Clear Filters
                </Button>
              )}
            </div>

            <PaginatedGentlyTable<CompanyDTO>
              data={data?.companies || []}
              columns={columns}
              isLoading={isLoading}
              searchPlaceholder={searchPlaceholder}
              onRowClick={handleRowClick}
              emptyMessage={emptyMessage}
              showSearch={true}
              showCount={true}
              itemName="companies"
              paginationConfig={paginationConfig}
              serverSideSearch={{
                value: pagination.searchQuery,
                onChange: pagination.setSearchQuery,
                debounceMs: 600,
              }}
              serverSideSorting={{
                sortBy: pagination.sortBy,
                sortDirection:
                  pagination.sortDirection === 'ASC' ? 'asc' : 'desc',
                onSort: (columnId, direction) => {
                  pagination.setSorting(
                    columnId,
                    direction === 'asc' ? 'ASC' : 'DESC',
                  );
                },
              }}
            />
          </CardContent>
        </Card>
      </Page>

      <CompanyCreateModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
        }}
        onSuccess={(company) => {
          refetch();
          navigate({ to: `/companies/${company.id}` });
        }}
        company={editingCompany}
        module={module}
      />

      {/* Bulk Upload Modal */}
      <Dialog
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh',
            height: 'auto',
            width: '95vw',
            maxWidth: '95vw',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h4">Bulk Company Upload</MDTypography>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', flex: 1 }}>
          <BulkCSVUpload
            title=""
            description="Upload a CSV file containing company information. Each row will be processed as a new company. Modules should be comma-separated (e.g., 'rebates,inventory')."
            expectedColumns={expectedColumns}
            onUploadRow={handleUploadRow}
            onComplete={handleBulkUploadComplete}
            maxRows={500}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setCompanyToDelete(undefined);
        }}
        onConfirm={confirmDeleteCompany}
        title="Delete Company"
        message={`Are you sure you want to delete "${companyToDelete?.name}"? This will permanently delete the company and ALL associated data including aliases and relationships. This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteCompanyMutation.isLoading}
        dangerous={true}
      />
    </>
  );
};

export default CompaniesPage;
